import React from 'react';
import { Link } from 'react-router-dom';
import SxWindow from '../SxWindow';
import { useVisibleTicker } from '../useVisibleTicker';
import {
  products,
  getProductDisplayName,
  STATUS_LABELS,
  type ProductStatus,
} from '../../../data/products';

/**
 * 状态色：只有内测中借用强调色（它是"正在发生的事"），
 * 已上线用中性白（稳定态不需要抢注意力），重构中用灰（明确降权）。
 */
const STATUS_COLOR: Record<ProductStatus, string> = {
  live: '#FFFFFF',
  beta: 'var(--sx-accent)',
  coming_soon: 'var(--sx-dim)',
  restructuring: 'var(--sx-mute)',
};

const TOC_COUNT = products.filter((p) => p.category === 'toc').length;
const TOB_COUNT = products.filter((p) => p.category === 'tob').length;

/**
 * 产品矩阵控制台 —— Hero 区的交互演示。
 *
 * 对齐参考站 Hero 下方"演示区"的位置，但内容换成本站最核心的事实：
 * 五款自研产品各自的形态与当前状态。数据直接来自 products.ts，
 * 因此产品线一旦变动这里会自动同步，不存在硬编码的过期风险。
 *
 * 品牌色点是全页唯一的多色使用——产品身份是真实信息，
 * 但压缩到 5px 的圆点，不破坏单强调色的整体秩序。
 */
const ProductMatrixConsole: React.FC = () => {
  const { ref, tick } = useVisibleTicker(2200);
  const scan = tick % products.length;

  return (
    <div ref={ref}>
      <SxWindow title="产品矩阵 · 运行状态" status="live" flush>
        <div>
          {products.map((product, i) => {
            const isScan = i === scan;
            const link = product.route ?? '/products';
            return (
              <Link
                key={product.id}
                to={link}
                className="group flex items-center gap-2.5 md:gap-4 px-3 md:px-4 py-2.5 md:py-3 transition-colors duration-500"
                style={{
                  background: isScan ? 'var(--sx-surface-2)' : 'transparent',
                  borderBottom: i < products.length - 1 ? '1px solid var(--sx-line)' : 'none',
                  borderLeft: `2px solid ${isScan ? 'var(--sx-accent)' : 'transparent'}`,
                  textDecoration: 'none',
                }}
              >
                <span className="sx-mono text-[9px] md:text-[10px] shrink-0 w-4">
                  {String(i + 1).padStart(2, '0')}
                </span>
                {/* 扫过的行才点亮：静态五色圆点会让列表读起来像五个不同的系统 */}
                <span
                  className="w-[5px] h-[5px] rounded-full shrink-0 transition-colors duration-500"
                  style={{ background: isScan ? 'var(--sx-accent)' : 'var(--sx-line-strong)' }}
                  aria-hidden="true"
                />
                <span className="text-xs md:text-sm font-medium text-white shrink-0">
                  {getProductDisplayName(product)}
                </span>
                <span
                  className="text-[11px] md:text-xs truncate hidden sm:block"
                  style={{ color: 'var(--sx-mute)' }}
                >
                  {product.tagline}
                </span>
                <span className="sx-mono sx-mono-plain text-[8px] md:text-[9px] ml-auto shrink-0 hidden md:block">
                  {product.platform}
                </span>
                <span
                  className="sx-mono text-[8px] md:text-[9px] shrink-0 ml-auto md:ml-0 w-14 md:w-16 text-right"
                  style={{ color: STATUS_COLOR[product.status] }}
                >
                  {STATUS_LABELS[product.status]}
                </span>
              </Link>
            );
          })}
        </div>

        {/* 汇总条 */}
        <div
          className="flex items-center justify-between px-3 md:px-4 py-2.5"
          style={{ borderTop: '1px solid var(--sx-line)', background: 'var(--sx-surface-2)' }}
        >
          <span className="sx-mono text-[9px]">
            {products.length} 款自研产品 · {TOC_COUNT} ToC · {TOB_COUNT} ToB
          </span>
          <span className="sx-mono text-[9px] sx-mono-accent hidden sm:block">
            全部自主研发
          </span>
        </div>
      </SxWindow>
    </div>
  );
};

export default ProductMatrixConsole;
